import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 */

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();
  #observer;
  #active = false;

  connectedCallback() {
    super.connectedCallback();

    this.handleScroll = debounce(this.handleScroll.bind(this), 60);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectObserver();
    this.#removeScroll();
  }

  /* ---------------------------------- */
  /* Open / Close                        */
  /* ---------------------------------- */

  async open(index, event) {
    event?.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    if (!targetImage) return;

    this.#active = true;
    this.#addScroll();

    const openDialog = () => {
      dialog.showModal();
      targetImage.scrollIntoView({ behavior: 'instant' });
      targetThumbnail?.scrollIntoView({ behavior: 'instant' });
    };

    const sourceImage =
      event?.target instanceof Element
        ? event.target.closest('li, slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage
    ) {
      openDialog();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const name = 'gallery-item';
    sourceImage.style.viewTransitionName = name;

    await startViewTransition(() => {
      openDialog();
      sourceImage.style.viewTransitionName = '';
      targetImage.style.viewTransitionName = name;
    });

    targetImage.style.viewTransitionName = '';
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  async close() {
    const { dialog, media } = this.refs;

    this.#active = false;
    this.#removeScroll();

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      this.closeDialog();
      return;
    }

    const visible = await this.#getMostVisible(media);
    const index = media.indexOf(visible);
    if (index === -1) return this.closeDialog();

    const gallery = this.closest('media-gallery');
    const slide =
      gallery?.presentation === 'carousel'
        ? gallery.slideshow?.slides?.[index]
        : gallery?.media?.[index];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    const name = 'gallery-item';
    visible.style.viewTransitionName = name;

    await startViewTransition(() => {
      visible.style.viewTransitionName = '';
      slide.style.viewTransitionName = name;
      this.closeDialog();
    });

    slide.style.viewTransitionName = '';
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /* ---------------------------------- */
  /* Scroll / Visibility                 */
  /* ---------------------------------- */

  #addScroll() {
    this.refs.dialog.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  #removeScroll() {
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    if (!this.#active) return;
    this.#updateVisibleMedia();
  }

  async #updateVisibleMedia() {
    const { media, thumbnails } = this.refs;
    const visible = await this.#getMostVisible(media);
    const index = media.indexOf(visible);
    if (index === -1) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) =>
      btn.setAttribute('aria-selected', `${i === index}`)
    );

    this.#loadHighResolutionImage(visible);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /* ---------------------------------- */
  /* Thumbnails                          */
  /* ---------------------------------- */

  async handleThumbnailClick(index) {
    this.selectThumbnail(index, {
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  async handleThumbnailPointerEnter(index) {
    const media = this.refs.media[index];
    if (media) this.#loadHighResolutionImage(media);
  }

  async selectThumbnail(index, { behavior = 'smooth' } = {}) {
    const { thumbnails, media } = this.refs;
    if (!thumbnails.children[index]) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) =>
      btn.setAttribute('aria-selected', `${i === index}`)
    );

    scrollIntoView(thumbnails.children[index], {
      ancestor: thumbnails,
      behavior,
      block: 'center',
      inline: 'center',
    });

    media[index]?.scrollIntoView({ behavior });
    this.#loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /* ---------------------------------- */
  /* High-res images                     */
  /* ---------------------------------- */

  #loadHighResolutionImage(container) {
    if (!container?.classList.contains('product-media-container--image')) return;

    const img = container.querySelector('img.product-media__image');
    const src = img?.getAttribute('data_max_resolution');
    if (!img || !src || this.#highResImagesLoaded.has(src)) return;

    preloadImage(src);

    const high = new Image();
    high.src = src;
    high.alt = img.alt;
    high.className = img.className;

    high.onload = () => {
      img.replaceWith(high);
      this.#highResImagesLoaded.add(src);
    };
  }

  /* ---------------------------------- */
  /* Visibility helper                   */
  /* ---------------------------------- */

  #getMostVisible(elements) {
    return new Promise((resolve) => {
      this.#disconnectObserver();

      this.#observer = new IntersectionObserver(
        (entries) => {
          const best = entries.reduce((a, b) =>
            b.intersectionRatio > a.intersectionRatio ? b : a
          );
          this.#disconnectObserver();
          resolve(best.target);
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      elements.forEach((el) => this.#observer.observe(el));
    });
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /* ---------------------------------- */
  /* Keyboard                            */
  /* ---------------------------------- */

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
